// BranchClaimProfileTab.tsx — Admin Panel -> Insurance Claims -> Branch
// Claims Profile. The hospital's own claim-identity particulars for the
// CURRENT branch (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.1, A1): ROHINI
// id, registration no./state code, PAN, NABH/NABL status, bed/OT/ICU
// facilities. Printed on every claim document's Section A ("Hospital
// particulars") / Section E (non-network block). Switch branches from the
// topbar branch switcher to edit a different branch's profile — same
// pattern every other per-branch admin setting in this app follows.
import { useEffect, useRef, useState } from 'react';
import { getBranchClaimProfile, updateBranchClaimProfile, getAllUsers, uploadBranchSeal, removeBranchSeal, fetchBranchSealUrl } from '../api';
import type { BranchClaimProfile, AdminUser } from '../types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { LoadingState } from '@/components/ui/States';

type Draft = {
  legal_name: string; rohini_id: string; registration_no: string; registration_state_code: string;
  pan: string; nabh_status: string; nabl_status: string; inpatient_beds: string;
  has_ot: boolean; has_icu: boolean; other_facilities: string; claims_email: string; claims_phone: string;
  authorised_signatory_user_id: string;
};

function toDraft(p: BranchClaimProfile): Draft {
  return {
    legal_name: p.legal_name ?? '', rohini_id: p.rohini_id ?? '', registration_no: p.registration_no ?? '',
    registration_state_code: p.registration_state_code ?? '', pan: p.pan ?? '', nabh_status: p.nabh_status ?? '',
    nabl_status: p.nabl_status ?? '', inpatient_beds: p.inpatient_beds != null ? String(p.inpatient_beds) : '',
    has_ot: !!p.has_ot, has_icu: !!p.has_icu, other_facilities: p.other_facilities ?? '',
    claims_email: p.claims_email ?? '', claims_phone: p.claims_phone ?? '',
    authorised_signatory_user_id: p.authorised_signatory_user_id != null ? String(p.authorised_signatory_user_id) : '',
  };
}

export default function BranchClaimProfileTab() {
  const [profile, setProfile] = useState<BranchClaimProfile | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Wireframe S9's "Authorised signatory": the person whose name prints in
  // the sealed block of the pre-auth request and Part B when a user holding
  // claims:sign-forms generates them (utils/claimExtract.js).
  const [users, setUsers] = useState<AdminUser[]>([]);
  useEffect(() => { getAllUsers().then(setUsers).catch(() => setUsers([])); }, []);

  useEffect(() => {
    getBranchClaimProfile()
      .then((p) => { setProfile(p); setDraft(toDraft(p)); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => d && { ...d, [key]: value });

  const save = async () => {
    if (!draft) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const updated = await updateBranchClaimProfile({
        legal_name: draft.legal_name.trim() || null,
        rohini_id: draft.rohini_id.trim() || null,
        registration_no: draft.registration_no.trim() || null,
        registration_state_code: draft.registration_state_code.trim().toUpperCase() || null,
        pan: draft.pan.trim().toUpperCase() || null,
        nabh_status: draft.nabh_status.trim() || null,
        nabl_status: draft.nabl_status.trim() || null,
        inpatient_beds: draft.inpatient_beds === '' ? null : Number(draft.inpatient_beds),
        has_ot: draft.has_ot,
        has_icu: draft.has_icu,
        other_facilities: draft.other_facilities.trim() || null,
        claims_email: draft.claims_email.trim() || null,
        claims_phone: draft.claims_phone.trim() || null,
        authorised_signatory_user_id: draft.authorised_signatory_user_id ? Number(draft.authorised_signatory_user_id) : null,
      });
      setProfile(updated);
      setDraft(toDraft(updated));
      setSaved(true);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  if (!profile || !draft) {
    return error ? <div className="rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div> : <LoadingState />;
  }

  return (
    <Card className="max-w-[640px] p-4">
      <div className="mb-1 text-sm font-semibold">Hospital claim identity — this branch</div>
      <div className="mb-4 text-xs text-ink-muted">
        Printed on every pre-authorisation request, Claim Form Part B and Main Bill this branch generates. A field
        left blank prints as &quot;Not recorded&quot; on those documents rather than blocking generation.
      </div>

      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      <div className="mb-3 grid grid-cols-2 gap-3">
        <FormField label="Legal name" className="col-span-2">
          <Input value={draft.legal_name} onChange={(e) => set('legal_name', e.target.value)} placeholder="Defaults to the branding hospital name if left blank" />
        </FormField>
        <FormField label="ROHINI ID">
          <Input value={draft.rohini_id} onChange={(e) => set('rohini_id', e.target.value)} />
        </FormField>
        <FormField label="Registration no.">
          <Input value={draft.registration_no} onChange={(e) => set('registration_no', e.target.value)} />
        </FormField>
        <FormField label="Registration state code">
          <Input value={draft.registration_state_code} onChange={(e) => set('registration_state_code', e.target.value)} placeholder="e.g. TN" maxLength={4} />
        </FormField>
        <FormField label="PAN">
          <Input value={draft.pan} onChange={(e) => set('pan', e.target.value)} placeholder="ABCDE1234F" maxLength={10} />
        </FormField>
        <FormField label="NABH status">
          <Input value={draft.nabh_status} onChange={(e) => set('nabh_status', e.target.value)} placeholder="e.g. Accredited" />
        </FormField>
        <FormField label="NABL status">
          <Input value={draft.nabl_status} onChange={(e) => set('nabl_status', e.target.value)} placeholder="e.g. Accredited" />
        </FormField>
        <FormField label="Inpatient beds">
          <Input type="number" min={0} value={draft.inpatient_beds} onChange={(e) => set('inpatient_beds', e.target.value)} />
        </FormField>
        <FormField label="Claims email">
          <Input type="email" value={draft.claims_email} onChange={(e) => set('claims_email', e.target.value)} />
        </FormField>
        <FormField label="Claims phone">
          <Input value={draft.claims_phone} onChange={(e) => set('claims_phone', e.target.value)} />
        </FormField>
      </div>

      <div className="mb-3 flex gap-4">
        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox checked={draft.has_ot} onCheckedChange={(v) => set('has_ot', !!v)} />
          Has OT
        </label>
        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox checked={draft.has_icu} onCheckedChange={(v) => set('has_icu', !!v)} />
          Has ICU
        </label>
      </div>

      <FormField label="Authorised signatory" className="mb-3">
        <NativeSelect value={draft.authorised_signatory_user_id} onChange={(e) => set('authorised_signatory_user_id', e.target.value)}>
          <option value="">Not set — forms print an empty seal box</option>
          {users.filter((u) => u.active !== false).map((u) => (
            <option key={u.id} value={u.id}>{u.full_name}{u.designation ? `, ${u.designation}` : ''} ({u.role})</option>
          ))}
        </NativeSelect>
        <div className="mt-1 text-[11px] text-ink-muted">
          Printed in the sealed hospital block of the pre-auth request and Claim Form Part B, only when the form is generated by
          someone holding <code>claims:sign-forms</code>.
        </div>
      </FormField>

      <SealImageField
        hasSeal={!!profile.seal_storage_key}
        uploadedAt={profile.seal_uploaded_at ?? null}
        onChanged={(p) => setProfile(p)}
      />

      <FormField label="Other facilities" className="mb-3">
        <Textarea value={draft.other_facilities} onChange={(e) => set('other_facilities', e.target.value)} placeholder="e.g. Cath lab, dialysis" rows={2} />
      </FormField>

      <Button size="sm" disabled={saving} loading={saving} onClick={save}>Save</Button>
      {saved && !error && (
        <div className="mt-2 rounded-[var(--radius)] border border-success-soft bg-success-soft px-3 py-2 text-xs text-success-ink">
          ✓ Saved.
        </div>
      )}
      {profile.updated_by && (
        <div className="mt-3 text-[11px] text-ink-muted">
          Last changed by {profile.updated_by} on {profile.updated_at && new Date(profile.updated_at).toLocaleString('en-IN')}
        </div>
      )}
    </Card>
  );
}

/** Wireframe S9 "Seal image": PNG/JPEG up to 1 MB, previewed here and printed
 * on generated forms only for a claims:sign-forms holder. Uploads/removes
 * immediately (its own endpoint) — not part of the profile Save above. */
function SealImageField({ hasSeal, uploadedAt, onChanged }: {
  hasSeal: boolean; uploadedAt: string | null; onChanged: (p: BranchClaimProfile) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    if (!hasSeal) { setUrl(null); return undefined; }
    fetchBranchSealUrl().then((u) => { objectUrl = u; if (alive) setUrl(u); }).catch(() => { if (alive) setUrl(null); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [hasSeal, uploadedAt]);

  const upload = async (file: File) => {
    setBusy(true); setErr('');
    try { onChanged(await uploadBranchSeal(file)); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true); setErr('');
    try { onChanged(await removeBranchSeal()); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };

  return (
    <FormField label="Seal image" className="mb-3">
      <div className="flex items-center gap-3">
        <div className="flex h-20 w-20 items-center justify-center rounded-full border border-dashed border-border bg-muted">
          {url ? <img src={url} alt="Hospital seal" className="max-h-[72px] max-w-[72px] object-contain" /> : <span className="text-[10px] text-ink-muted">No seal</span>}
        </div>
        <div className="flex flex-col gap-1.5">
          <input
            ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}
          />
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" loading={busy} onClick={() => inputRef.current?.click()}>📎 {hasSeal ? 'Replace' : 'Upload'} seal</Button>
            {hasSeal && <Button size="sm" variant="ghost" disabled={busy} onClick={remove}>Remove</Button>}
          </div>
          <div className="text-[11px] text-ink-muted">
            PNG or JPEG, up to 1 MB (a transparent PNG prints best).
            {uploadedAt ? ` Uploaded ${new Date(uploadedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.` : ''}
          </div>
          {err && <div className="text-[11px] text-danger-ink">{err}</div>}
        </div>
      </div>
    </FormField>
  );
}
